import './DatePicker.scss';

import React, { useEffect } from 'react';

import type { CellRenderInfo } from 'rc-picker/es/interface';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import Icon from '@/components/Icon/Icon';
import { formatMonthName } from '@/utilities/DateUtils';

type IDatePickerProps = DatePickerProps & {
  /** Field level error message */
  fieldErrMessg?: string;
  /** Called when the user focuses of the input. */
  handleBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  /** Label for the date picker */
  label?: string;
  /** Whether to use floating label */
  floatingLabel?: boolean;
  /** The className of dropdown menu. */
  popupClassName?: string;
};

/**
 * custom cell render for date picker (Ex green dot on right top for current date)
 */
const dotStyle: React.CSSProperties = {
  position: 'absolute',
  top: '2px',
  right: '0px',
  width: '5px',
  height: '5px',
  background: '#38B896',
  borderRadius: '50%',
};

/**
 *
 * @returns placeholder for date picker
 */
const getPlaceHolder = () => {
  const date = new Date();
  const monthName = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const formattedDate = `e.g. ${formatMonthName(monthName)} ${day}, ${year}`;
  return formattedDate;
};

/**
 * Make past dates and weekends
 * @param current
 * @param info
 * @returns Customized cell render for date picker
 */
const customCellRender = (current: dayjs.Dayjs, info: CellRenderInfo<dayjs.Dayjs>) => {
  if (info.type !== 'date') {
    return info.originNode;
  }
  if (!current.isValid()) {
    return <div className="ant-picker-cell-inner">{String(current)}</div>;
  }
  return (
    <div className="ant-picker-cell-inner">
      {String(current.date())}
      {current.isSame(dayjs(), 'day') && <div style={dotStyle} />}
    </div>
  );
};

const DatePickerComponent: React.FC<IDatePickerProps> = React.forwardRef<
  HTMLDivElement,
  IDatePickerProps
>((props: IDatePickerProps, ref) => {
  const {
    onChange,
    suffixIcon = <Icon source="/src/assets/svg/invoices/calendar.svg" />,
    size = 'large',
    placeholder = getPlaceHolder(),
    format = 'MMM DD, YYYY',
    status,
    cellRender = customCellRender,
    disabled = false,
    fieldErrMessg = '',
    placement,
    getPopupContainer,
    popupClassName,
    label,
    floatingLabel = false,
    value,
    open,
    onOpenChange,
    disabledDate: disabledDateProp,
  } = props;
  const [datePickerStatus, setDatePickerStatus] = React.useState(status);
  const [isFocused, setIsFocused] = React.useState(false);

  const defaultDisabledDate: RangePickerProps['disabledDate'] = (current) =>
    Boolean(
      current &&
        (current.isBefore(dayjs().startOf('day')) || current.day() === 0 || current.day() === 6)
    );

  const disabledDate = disabledDateProp ?? defaultDisabledDate;

  useEffect(() => {
    setDatePickerStatus(status);
  }, [status]);

  /**
   * Method to update the status of the date picker.
   *
   * @param event - Event object
   */
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    // Defer so calendar panel clicks register before blur validation runs.
    window.setTimeout(() => {
      const openDropdown = document.querySelector(
        '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)'
      );
      if (openDropdown) {
        return;
      }

      if (event.target && !event.target.value) {
        setDatePickerStatus('error');
      } else {
        setDatePickerStatus('');
      }

      props.handleBlur?.(event);
    }, 0);
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const hasValue = value !== null && value !== undefined;
  const shouldFloatLabel = floatingLabel && (isFocused || hasValue);
  const hasSuffixIcon = suffixIcon !== null && suffixIcon !== undefined;

  return (
    <div
      ref={ref}
      className={`date-picker-wrapper ${floatingLabel ? 'floating-label' : ''} ${
        hasSuffixIcon ? '' : 'date-picker-wrapper--no-suffix'
      }`}
    >
      {label && floatingLabel && (
        <label className={`date-picker-label ${shouldFloatLabel ? 'floated' : ''}`}>{label}</label>
      )}
      {label && !floatingLabel && <label className="date-picker-label-static">{label}</label>}
      <DatePicker
        status={datePickerStatus}
        format={format}
        onChange={onChange}
        disabledDate={disabledDate}
        suffixIcon={suffixIcon}
        size={size}
        cellRender={cellRender}
        placeholder={placeholder}
        showToday={false}
        disabled={disabled}
        onBlur={onBlur}
        onFocus={onFocus}
        placement={placement}
        getPopupContainer={getPopupContainer}
        popupClassName={popupClassName}
        value={value}
        open={open}
        onOpenChange={onOpenChange}
      />
      <div role="alert" className="err-messg-container">
        {fieldErrMessg}
      </div>
    </div>
  );
});

DatePickerComponent.displayName = 'DatePicker';

export default DatePickerComponent;
